import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ArrowLeft, PhoneCall, PhoneMissed, CheckCircle2, AlertTriangle, Loader2, Trash2 } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDateTime = (iso) => {
    if (!iso) return '—';
    try {
        return format(new Date(iso), 'dd/MM/yyyy à HH:mm', { locale: fr });
    } catch { return iso; }
};

const formatHeure = (iso) => {
    if (!iso) return '—';
    try {
        return format(new Date(iso), 'HH:mm', { locale: fr });
    } catch { return iso; }
};

const STATUT_META = {
    a_temps: { label: 'À temps', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500', icon: CheckCircle2, iconColor: 'text-emerald-600', iconBg: 'bg-emerald-50' },
    en_retard: { label: 'En retard', color: 'bg-orange-50 text-orange-700 border-orange-200', dot: 'bg-orange-500', icon: AlertTriangle, iconColor: 'text-orange-600', iconBg: 'bg-orange-50' },
    non_appelee: { label: 'Non appelée', color: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500', icon: PhoneMissed, iconColor: 'text-red-600', iconBg: 'bg-red-50' },
    en_attente: { label: 'En attente', color: 'bg-slate-50 text-slate-600 border-slate-200', dot: 'bg-slate-400', icon: Loader2, iconColor: 'text-slate-500', iconBg: 'bg-slate-50' },
};

export default function VisitesRetardPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    const [visites, setVisites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deletingId, setDeletingId] = useState(null);

    const fetchData = async () => {
        try {
            const { data } = await axios.get(`${API}/api/visites/retard`, { withCredentials: true });
            setVisites(data || []);
        } catch (e) {
            setVisites([]);
        } finally { setLoading(false); }
    };

    const handleDelete = async (v) => {
        if (!window.confirm(`Masquer la visite virtuelle de ${v.client_nom} de cette liste ?\n\nElle restera intacte dans le dossier du client (prospect, devis, calendrier...).`)) return;
        setDeletingId(v.id);
        try {
            await axios.post(`${API}/api/visites/retard/${v.id}/hide`, {}, { withCredentials: true });
            toast.success('Visite masquée de la liste');
            setVisites(prev => prev.filter(x => x.id !== v.id));
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur lors du masquage');
        } finally {
            setDeletingId(null);
        }
    };

    useEffect(() => {
        fetchData();
        const t = setInterval(fetchData, 60000);
        return () => clearInterval(t);
    }, []);

    const counts = visites.reduce((acc, v) => {
        acc[v.statut] = (acc[v.statut] || 0) + 1;
        return acc;
    }, {});

    // Tri : juste par date la plus recente en haut (sans priorite de statut)
    const visitesSorted = [...visites].sort((a, b) => {
        const ka = a.visite_at || `${a.date || ''}T${a.heure || '00:00'}`;
        const kb = b.visite_at || `${b.date || ''}T${b.heure || '00:00'}`;
        if (ka === kb) return 0;
        return ka < kb ? 1 : -1; // plus recent en haut
    });

    return (
        <div className="min-h-screen bg-slate-50 px-6 py-6">
            <div className="max-w-7xl mx-auto space-y-5">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="retard-back">
                            <ArrowLeft size={16} className="mr-1" /> Accueil
                        </Button>
                        <div>
                            <h1 className="text-xl md:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Visites en retard
                            </h1>
                            <p className="text-xs text-slate-500">Suivi des appels passés sur les visites virtuelles · 5 derniers jours</p>
                        </div>
                    </div>
                </div>

                {/* Stats compteurs */}
                <Card data-testid="retard-stats-card">
                    <CardContent className="pt-5 pb-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-100">
                                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                                    <CheckCircle2 size={20} className="text-emerald-600" />
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-emerald-700 tabular-nums">{counts.a_temps || 0}</div>
                                    <div className="text-xs text-emerald-700/80">à temps</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 rounded-xl bg-orange-50 border border-orange-100">
                                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                                    <AlertTriangle size={20} className="text-orange-600" />
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-orange-700 tabular-nums">{counts.en_retard || 0}</div>
                                    <div className="text-xs text-orange-700/80">en retard</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 rounded-xl bg-red-50 border border-red-100">
                                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                                    <PhoneMissed size={20} className="text-red-600" />
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-red-700 tabular-nums">{counts.non_appelee || 0}</div>
                                    <div className="text-xs text-red-700/80">non appelée(s)</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-100 border border-slate-200">
                                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                                    <Loader2 size={20} className="text-slate-500" />
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-slate-700 tabular-nums">{counts.en_attente || 0}</div>
                                    <div className="text-xs text-slate-600/80">en attente</div>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Tableau */}
                <Card data-testid="retard-list-card">
                    <CardHeader className="pb-3 border-b border-slate-100">
                        <CardTitle className="text-base font-semibold text-slate-700">Détail des visites virtuelles</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                        {loading ? (
                            <p className="text-sm text-slate-400 italic text-center py-6">Chargement…</p>
                        ) : visites.length === 0 ? (
                            <p className="text-sm text-slate-400 italic text-center py-6">Aucune visite virtuelle dans les 5 derniers jours</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <Table className="min-w-[820px]">
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-12"></TableHead>
                                            <TableHead>Client</TableHead>
                                            <TableHead>Téléphone</TableHead>
                                            <TableHead>Visite prévue</TableHead>
                                            <TableHead>1er appel</TableHead>
                                            <TableHead className="text-center">Délai</TableHead>
                                            <TableHead className="text-center">Nb appels</TableHead>
                                            <TableHead className="text-right">Statut</TableHead>
                                            {isAdmin && <TableHead className="text-right w-16"></TableHead>}
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {visitesSorted.map(v => {
                                            const meta = STATUT_META[v.statut] || STATUT_META.en_attente;
                                            const Icon = meta.icon;
                                            const rowBg = v.statut === 'non_appelee'
                                                ? 'bg-red-50/30 hover:bg-red-50/60'
                                                : v.statut === 'en_retard'
                                                    ? 'bg-orange-50/30 hover:bg-orange-50/60'
                                                    : 'hover:bg-slate-50';
                                            return (
                                                <TableRow key={v.id} data-testid={`retard-${v.id}`} className={rowBg}>
                                                    <TableCell className="w-12">
                                                        <div className={`w-9 h-9 rounded-full ${meta.iconBg} flex items-center justify-center`}>
                                                            <Icon size={16} className={meta.iconColor} />
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <button
                                                            onClick={() => v.prospect_id && navigate(`/prospects/${v.prospect_id}`)}
                                                            className="font-medium text-[#2e7cd6] hover:underline text-left"
                                                        >
                                                            {v.client_nom}
                                                        </button>
                                                    </TableCell>
                                                    <TableCell className="text-slate-700 font-mono text-sm">{v.telephone || '—'}</TableCell>
                                                    <TableCell className="text-slate-700 text-sm tabular-nums">
                                                        {v.date && v.heure ? `${v.date.split('-').reverse().join('/')} à ${v.heure}` : '—'}
                                                    </TableCell>
                                                    <TableCell className="text-slate-600 text-sm">
                                                        {v.first_call_at ? formatDateTime(v.first_call_at) : <span className="text-slate-400 italic">—</span>}
                                                    </TableCell>
                                                    <TableCell className="text-center text-sm tabular-nums">
                                                        {v.delai_minutes !== null && v.delai_minutes !== undefined ? (
                                                            <span className={v.delai_minutes <= 5 ? 'text-emerald-700 font-medium' : 'text-orange-700 font-medium'}>
                                                                {v.delai_minutes < 0
                                                                    ? `${Math.abs(v.delai_minutes)} min en avance`
                                                                    : v.delai_minutes === 0
                                                                        ? 'À l\'heure'
                                                                        : `+${v.delai_minutes} min`}
                                                            </span>
                                                        ) : (
                                                            <span className="text-slate-400">—</span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-center">
                                                        <Badge variant="outline" className="text-xs">
                                                            {v.nb_appels}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <Badge variant="outline" className={`text-xs ${meta.color}`}>
                                                            {meta.label}
                                                        </Badge>
                                                    </TableCell>
                                                    {isAdmin && (
                                                        <TableCell className="text-right w-16">
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                                                                onClick={() => handleDelete(v)}
                                                                disabled={deletingId === v.id}
                                                                title="Masquer de cette liste (admin)"
                                                                data-testid={`retard-delete-${v.id}`}
                                                            >
                                                                {deletingId === v.id
                                                                    ? <Loader2 size={14} className="animate-spin" />
                                                                    : <Trash2 size={14} />}
                                                            </Button>
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
